// MARK://dhk8/admin-shell/pages/modules/RecipesPage
// Recetario standalone — lista de recetas → dhk8-catalog-recipes (en prod).
// Portado de dhk8-admin `/recetario/recetas` al sistema HIG-green (adm-*) de
// admin-shell. Presentación DS: DataTable + FilterBar + SummaryStrip + StatusPill.
// Borrado con ConfirmDialog (useConfirm). Crear receta refresca la lista y abre
// la ficha (fix: antes navegaba sin refrescar → al volver la lista salía vieja).
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, ChefHat, Copy, CheckCircle2, Trash2 } from 'lucide-react'
import * as rec from '../../lib/clients/recipes-catalog.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'
import FieldHelp from '../../components/FieldHelp.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

const STATUS_TONE: Record<rec.RecipeStatus, StatusTone> = {
  draft: 'warn',
  published: 'ok',
  archived: 'neutral',
}

function mxn(cents: number | null): string {
  if (cents == null) return '—'
  return (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}

type StatusFilter = 'all' | rec.RecipeStatus

interface CreateForm { name: string; yieldServings: number; prepTimeMin: number; kdsStation: string }
const emptyForm = (): CreateForm => ({ name: '', yieldServings: 1, prepTimeMin: 0, kdsStation: '' })

export default function RecipesPage() {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { toast } = useToast()
  const list = useAsync((s) => rec.listRecipes(s))

  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<CreateForm>(emptyForm())
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState(false)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [sort, setSort] = useState<SortState>({ key: 'name', dir: 'asc' })

  const stats = useMemo(() => {
    const items = list.data ?? []
    const publicadas = items.filter((r) => r.status === 'published').length
    const borradores = items.filter((r) => r.status === 'draft').length
    const costs = items.map((r) => r.cost ?? 0).filter((c) => c > 0)
    const avg = costs.length ? Math.round(costs.reduce((a, b) => a + b, 0) / costs.length) : null
    return { total: items.length, publicadas, borradores, avg }
  }, [list.data])

  const rows = useMemo(() => {
    const items = list.data ?? []
    const term = search.trim().toLowerCase()
    const filtered = items.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false
      if (term && !r.name.toLowerCase().includes(term) && !(r.kdsStation ?? '').toLowerCase().includes(term)) return false
      return true
    })
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      switch (sort.key) {
        case 'cost': return ((a.cost ?? -1) - (b.cost ?? -1)) * dir
        case 'ingredients': return (a.ingredients.length - b.ingredients.length) * dir
        case 'status': return a.status.localeCompare(b.status) * dir
        default: return a.name.localeCompare(b.name, 'es') * dir
      }
    })
  }, [list.data, search, statusFilter, sort])

  function openCreate() { setForm(emptyForm()); setFormError(null); setCreating(true) }

  async function saveCreate() {
    setFormError(null)
    if (!form.name.trim()) { setFormError('El nombre es obligatorio.'); return }
    setSaving(true)
    try {
      const created = await rec.createRecipe({
        name: form.name.trim(),
        yieldServings: form.yieldServings || 1,
        ...(form.prepTimeMin ? { prepTimeMin: form.prepTimeMin } : {}),
        ...(form.kdsStation ? { kdsStation: form.kdsStation } : {}),
      })
      setCreating(false)
      // Fix: refresca la lista antes de navegar para que, al volver, esté al día.
      list.reload()
      navigate(`/recipes/${created.id}`)
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo crear la receta.')
    } finally { setSaving(false) }
  }

  async function publish(id: string) {
    setBusy(true)
    try { await rec.publishRecipe(id); list.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo publicar.', 'danger') }
    finally { setBusy(false) }
  }
  async function duplicate(id: string) {
    setBusy(true)
    try { const d = await rec.duplicateRecipe(id); list.reload(); navigate(`/recipes/${d.id}`) }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo duplicar.', 'danger') }
    finally { setBusy(false) }
  }
  async function remove(r: rec.Recipe) {
    const ok = await confirm({
      title: '¿Eliminar receta?',
      message: `Se eliminará “${r.name}”. Esta acción no se puede deshacer.`,
      tone: 'danger',
      confirmLabel: 'Eliminar',
    })
    if (!ok) return
    setBusy(true)
    try { await rec.deleteRecipe(r.id); list.reload(); toast('Receta eliminada.', 'success') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar.', 'danger') }
    finally { setBusy(false) }
  }

  const columns: DataTableColumn<rec.Recipe>[] = [
    {
      key: 'name', header: 'Receta', sortable: true,
      render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ChefHat size={14} aria-hidden style={{ opacity: 0.6, flexShrink: 0 }} />
          <span style={{ fontWeight: 500 }}>{r.name}</span>
        </span>
      ),
    },
    {
      key: 'status', header: 'Estado', sortable: true,
      render: (r) => <StatusPill tone={STATUS_TONE[r.status]} label={rec.recipeStatusLabel(r.status)} />,
    },
    {
      key: 'ingredients', header: 'Ingredientes', align: 'right', sortable: true,
      render: (r) => <span className="adm-mono">{r.ingredients.length}</span>,
    },
    {
      key: 'cost', header: 'Costo', align: 'right', sortable: true,
      render: (r) => <span className="adm-mono">{mxn(r.cost)}</span>,
    },
    {
      key: 'station', header: 'Estación',
      render: (r) => <span className="adm-muted">{r.kdsStation || '—'}</span>,
    },
    {
      key: 'actions', header: '', align: 'right', width: '120px',
      render: (r) => (
        <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          {r.status === 'draft' && (
            <button className="adm-btn adm-btn--ghost adm-btn--sm" disabled={busy} onClick={() => publish(r.id)} aria-label="Publicar" title="Publicar"><CheckCircle2 size={15} /></button>
          )}
          <button className="adm-btn adm-btn--ghost adm-btn--sm" disabled={busy} onClick={() => duplicate(r.id)} aria-label="Duplicar" title="Duplicar"><Copy size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={busy} onClick={() => remove(r)} aria-label="Eliminar" title="Eliminar"><Trash2 size={15} /></button>
        </span>
      ),
    },
  ]

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Recetas</h1><p className="adm-page__sub">Escandallo, costeo, alérgenos y estación de cocina</p></div>
        <div className="adm-page__actions">
          <button className="adm-btn adm-btn--primary" onClick={openCreate}><Plus size={16} /> Nueva receta</button>
        </div>
      </div>

      {list.data && (
        <div style={{ marginBottom: 16 }}>
          <SummaryStrip items={[
            { label: 'Recetas', value: stats.total },
            { label: 'Publicadas', value: stats.publicadas, tone: 'ok' },
            { label: 'Borradores', value: stats.borradores, tone: stats.borradores > 0 ? 'warn' : 'neutral' },
            { label: 'Costo promedio', value: mxn(stats.avg) },
          ]} />
        </div>
      )}

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <DataTable<rec.Recipe>
          columns={columns}
          rows={rows}
          getRowKey={(r) => r.id}
          zebra
          sort={sort}
          onSortChange={setSort}
          onRowClick={(r) => navigate(`/recipes/${r.id}`)}
          emptyText={list.data.length === 0 ? 'Aún no hay recetas. Crea la primera para empezar a costear.' : 'Ninguna receta coincide con el filtro.'}
          toolbar={
            <FilterBar
              search={search}
              onSearch={setSearch}
              searchPlaceholder="Buscar receta o estación…"
              filters={[{
                key: 'status', label: 'Estado', value: statusFilter,
                onChange: (v) => setStatusFilter(v as StatusFilter),
                options: [
                  { value: 'all', label: 'Todos' },
                  { value: 'draft', label: 'Borrador' },
                  { value: 'published', label: 'Publicada' },
                  { value: 'archived', label: 'Archivada' },
                ],
              }]}
              actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openCreate}><Plus size={15} /> Nueva receta</button>}
            />
          }
        />
      )}

      <Sheet
        open={creating}
        title="Nueva receta"
        onClose={() => { setCreating(false); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreating(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveCreate} disabled={saving || !form.name.trim()}>{saving ? 'Guardando…' : 'Crear receta'}</button>
        </>}
      >
        {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}
        <label className="adm-field">
          <FieldHelp entity="Recipe" field="name" label="Nombre" />
          <input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Cappuccino" />
        </label>
        <label className="adm-field">
          <FieldHelp entity="Recipe" field="yield" label="Rendimiento (porciones)" />
          <input className="adm-input" type="number" min={1} value={form.yieldServings} onChange={(e) => setForm({ ...form, yieldServings: Number(e.target.value) })} />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Tiempo de preparación (min)</span>
          <input className="adm-input" type="number" min={0} value={form.prepTimeMin} onChange={(e) => setForm({ ...form, prepTimeMin: Number(e.target.value) })} />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Estación de cocina</span>
          <select className="adm-select" value={form.kdsStation} onChange={(e) => setForm({ ...form, kdsStation: e.target.value })} aria-label="Estación de cocina">
            <option value="">— Sin asignar —</option>
            {rec.KDS_STATIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
      </Sheet>
    </div>
  )
}
